import React, { memo, useMemo } from 'react';
import { View, Text, TouchableOpacity, Switch, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagListItemProps {
  tag: CorkBoardTag;
  onToggleEnabled: (tag: CorkBoardTag) => void;
  onEdit: (tag: CorkBoardTag) => void;
  onDelete: (tag: CorkBoardTag) => void;
  isFirst?: boolean;
  isLast?: boolean;
  onMoveUp?: (tagId: number) => void;
  onMoveDown?: (tagId: number) => void;
}

const arePropsEqual = (prev: TagListItemProps, next: TagListItemProps): boolean =>
  prev.tag.id === next.tag.id &&
  prev.tag.name === next.tag.name &&
  prev.tag.color === next.tag.color &&
  prev.tag.icon === next.tag.icon &&
  prev.tag.isEnabled === next.tag.isEnabled &&
  prev.isFirst === next.isFirst &&
  prev.isLast === next.isLast;

export const TagListItem: React.FC<TagListItemProps> = memo(
  ({ tag, onToggleEnabled, onEdit, onDelete, isFirst, isLast, onMoveUp, onMoveDown }) => {
    const tagColor = tag.color || '#6B7280';

    const swatchStyle = useMemo(
      () => [styles.colorSwatch, { backgroundColor: tagColor }],
      [tagColor],
    );

    return (
      <View
        style={[
          styles.container,
          !tag.isEnabled && styles.containerDisabled,
        ]}
      >
        <View style={styles.topRow}>
          <View style={swatchStyle} />
          {tag.icon ? <Text style={styles.icon}>{tag.icon}</Text> : null}
          <View style={styles.nameContainer}>
            <Text style={[styles.name, !tag.isEnabled && styles.nameDisabled]} numberOfLines={1}>
              {tag.name}
            </Text>
            {!tag.isEnabled && <Text style={styles.disabledBadge}>Disabled</Text>}
          </View>
          <Switch
            value={tag.isEnabled}
            onValueChange={() => onToggleEnabled(tag)}
            trackColor={{ false: ADMIN_COLORS.BORDER_SECONDARY, true: ADMIN_COLORS.PRIMARY }}
            thumbColor="#FFFFFF"
            ios_backgroundColor={ADMIN_COLORS.BORDER_SECONDARY}
            accessibilityLabel={`${tag.isEnabled ? 'Disable' : 'Enable'} ${tag.name}`}
          />
        </View>

        <View style={styles.bottomRow}>
          <View style={styles.reorderButtons}>
            <TouchableOpacity
              style={[styles.arrowButton, isFirst && styles.arrowButtonDisabled]}
              onPress={() => onMoveUp?.(tag.id)}
              disabled={isFirst}
              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              accessibilityLabel={`Move ${tag.name} up`}
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.CHEVRON_UP width={12} height={12} color={isFirst ? ADMIN_COLORS.TEXT_TERTIARY : ADMIN_COLORS.TEXT.PRIMARY} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.arrowButton, isLast && styles.arrowButtonDisabled]}
              onPress={() => onMoveDown?.(tag.id)}
              disabled={isLast}
              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
              accessibilityLabel={`Move ${tag.name} down`}
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.CHEVRON_DOWN width={12} height={12} color={isLast ? ADMIN_COLORS.TEXT_TERTIARY : ADMIN_COLORS.TEXT.PRIMARY} />
            </TouchableOpacity>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onEdit(tag)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityLabel={`Edit ${tag.name}`}
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.PENCIL width={14} height={14} color={ADMIN_COLORS.PRIMARY} />
              <Text style={styles.actionText}>Edit</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => onDelete(tag)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityLabel={`Delete ${tag.name}`}
              accessibilityRole="button"
            >
              <SVG_COMPONENTS.TRASH width={14} height={14} color={ADMIN_COLORS.ERROR} />
              <Text style={styles.deleteText}>Delete</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  },
  arePropsEqual,
);

TagListItem.displayName = 'TagListItem';

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    gap: 10,
  },
  containerDisabled: {
    opacity: 0.6,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  colorSwatch: {
    width: 14,
    height: 14,
    borderRadius: 4,
  },
  icon: {
    fontSize: 18,
  },
  nameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: 15,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    flexShrink: 1,
  },
  nameDisabled: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  disabledBadge: {
    fontSize: 10,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_TERTIARY,
    backgroundColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden',
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  reorderButtons: {
    flexDirection: 'row',
    gap: 6,
  },
  arrowButton: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  arrowButtonDisabled: {
    opacity: 0.3,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  actionText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
  },
  deleteText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.ERROR,
  },
});
